import type { GroceryItem, GroceryList } from "./api/types";

export interface GroceryListCompletedProperties {
  itemCount: number;
  mealPlanId: string;
}

export interface GroceryCheckoffAnalyticsSnapshot {
  mealPlanId: string;
  generatedItemIds: string[];
  uncheckedGeneratedItemIds: string[];
}

export function isGroceryList(value: unknown): value is GroceryList {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<GroceryList>;
  return typeof candidate.id === "string" && Array.isArray(candidate.items);
}

function visibleGeneratedItems(list: GroceryList): GroceryItem[] {
  return list.items.filter(
    (item) => item.sourceRecipeIds.length > 0 && item.removed !== true,
  );
}

export function snapshotGroceryCheckoffAnalytics(
  list: GroceryList,
): GroceryCheckoffAnalyticsSnapshot {
  const generatedItems = visibleGeneratedItems(list);
  return {
    mealPlanId: list.mealPlanId,
    generatedItemIds: generatedItems.map((item) => item.id),
    uncheckedGeneratedItemIds: generatedItems
      .filter((item) => !item.checked)
      .map((item) => item.id),
  };
}

export function advanceGroceryCheckoffAnalytics(
  previous: GroceryCheckoffAnalyticsSnapshot | undefined,
  itemId: string,
  checked: boolean,
): {
  next: GroceryCheckoffAnalyticsSnapshot | undefined;
  completion: GroceryListCompletedProperties | null;
} {
  if (!previous) return { next: undefined, completion: null };

  const generatedItemIds = new Set(previous.generatedItemIds);
  const uncheckedBefore = new Set(previous.uncheckedGeneratedItemIds);
  const uncheckedAfter = new Set(uncheckedBefore);
  if (generatedItemIds.has(itemId)) {
    if (checked) uncheckedAfter.delete(itemId);
    else uncheckedAfter.add(itemId);
  }

  const next = {
    ...previous,
    uncheckedGeneratedItemIds: [...uncheckedAfter],
  };
  const completion =
    checked && uncheckedBefore.size > 0 && uncheckedAfter.size === 0
      ? {
          itemCount: previous.generatedItemIds.length,
          mealPlanId: previous.mealPlanId,
        }
      : null;

  return { next, completion };
}

/**
 * Describe the completion transition caused by one confirmed check-off.
 * Manual and removed lines do not count toward generated-list completion.
 */
export function completionAfterConfirmedCheckoff(
  previousList: GroceryList | undefined,
  itemId: string,
  checked: boolean,
): GroceryListCompletedProperties | null {
  if (!previousList) return null;
  return advanceGroceryCheckoffAnalytics(
    snapshotGroceryCheckoffAnalytics(previousList),
    itemId,
    checked,
  ).completion;
}
